/**
 * Right-column editor for the selected stage — four cards per
 * admin_schedule_mvp.html:
 *   1. Identity — big mono SS/H label, name input, Stage|Circuit
 *      segmented toggle (+ Delete stage).
 *   2. Schedule — Stage: first-car HH:MM + car-interval select +
 *      safety-timeout minutes. Circuit: heat-start HH:MM + laps +
 *      timeout. Header right shows the event timezone.
 *   3. Gate panes — compact paste-friendly GatePane pair, placed
 *      ABOVE the map so coordinates are visible and editable without
 *      scrolling past the tall map card (they used to sit at the
 *      bottom of the page).
 *   4. Map — REUSED editors: <CircuitMapEditor> for Circuit stages
 *      (exactly as the old StagesTab rendered it — its lap-line saves
 *      keep skipping the revision bump), GateMapPicker click-to-place
 *      for rally stages.
 *
 * All edits land in the parent-owned StageFormState; persistence
 * happens in StagesTab's Save flow (per-gate POST/PUT/DELETE diff +
 * stages_json upsert).
 */

import React from 'react';
import { Trash2, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CircuitMapEditor from '@/components/CircuitMapEditor';
import { GatePos } from '@/components/GateMapPicker';
import { cn } from '@/lib/utils';
import {
  EventRow,
  StageRouteInfo,
  deleteStageRoute,
  getStageRoute,
  putStageRoute,
} from '@/services/fleet';
import useStageSessions from '@/hooks/useStageSessions';

import { EVENT_TYPES } from '../EventTab';
import GatePane from './GatePane';
import StageFootageCard from './StageFootageCard';
import {
  CAR_INTERVAL_OPTIONS,
  StageFormState,
  StageType,
  StageView,
  TIME_RE,
  TIMEOUT_MAX_MINUTES,
  TIMEOUT_MIN_MINUTES,
  clamp,
  stageLabel,
} from './stageModel';

interface StageEditorPaneProps {
  event: EventRow;
  view: StageView;
  form: StageFormState;
  onFormChange: (next: StageFormState) => void;
  onDeleteStage: () => void;
  /** Threaded into CircuitMapEditor's onSaved (its saves bypass our Save flow). */
  onRefresh: () => Promise<void> | void;
  /** Stage 0 only: patches event.type directly (own PUT, bypasses the
   *  stage form's Save flow — everything real keys off this field, not
   *  any per-stage metadata). */
  onEventTypeChange: (type: string) => void | Promise<void>;
  eventTypeSaving?: boolean;
}

export const StageEditorPane: React.FC<StageEditorPaneProps> = ({
  event,
  view,
  form,
  onFormChange,
  onDeleteStage,
  onRefresh,
  onEventTypeChange,
  eventTypeSaving = false,
}) => {
  const isCircuit = form.type === 'Circuit';
  const label = stageLabel(view.index, form.type);
  const patch = (p: Partial<StageFormState>) => onFormChange({ ...form, ...p });
  const setStart = (next: GatePos) => patch({ start: next });
  const setFinish = (next: GatePos) => patch({ finish: next });

  // A stage needs at least ONE start trigger: a start gate, a start
  // time (schedule), or a corridor route. Computed from the unsaved
  // form so placing a pin / typing a time clears the warning at once;
  // the route flag comes from the saved view (routes save immediately
  // in StageCorridorCard, outside the form's Save flow).
  const startGateSet = form.start.lat != null && form.start.lon != null;
  const finishGateSet = form.finish.lat != null && form.finish.lon != null;
  const startTimeSet = TIME_RE.test(form.startTime.trim());
  const noStartTrigger = !startGateSet && !startTimeSet && !view.hasRoute;

  return (
    <div className="flex flex-col gap-3.5">
      {noStartTrigger && (
        <WarnBanner>
          <b>No start trigger</b>
          {' — place a start gate, set a start time, or upload a corridor route. '}
          The stage stays Draft (phones can’t start it) until it has one.
        </WarnBanner>
      )}
      {!noStartTrigger && !finishGateSet && !view.hasRoute && !isCircuit && (
        <WarnBanner>
          <b>No finish gate</b>
          {` — recording will only stop by the safety timeout (${form.timeoutMinutes} min), not at a finish line.`}
        </WarnBanner>
      )}
      {view.reverseOf != null && (
        <WarnBanner>
          Route copied from SS{view.reverseOf + 1} (reversed) — gates don’t copy, place
          them fresh.
        </WarnBanner>
      )}

      {/* 1 — Identity */}
      <Card className="p-4">
        <CardContent className="flex flex-wrap items-center gap-3 p-0">
          <span
            className={cn(
              'font-mono text-[22px] font-bold leading-none',
              isCircuit ? 'text-accent-cyan' : 'text-accent',
            )}
          >
            {label}
          </span>
          <Input
            value={form.name}
            onChange={(e) => patch({ name: e.target.value })}
            maxLength={64}
            placeholder="Stage name"
            className="min-w-[200px] flex-1 text-base font-semibold"
          />
          {view.index === 0 ? (
            <EventTypeSelect
              value={event.type ?? 'Rally'}
              onChange={onEventTypeChange}
              disabled={eventTypeSaving}
            />
          ) : (
            <div className="flex items-center gap-2">
              <SegmentedTypeToggle value={form.type} disabled />
              <span className="text-[10px] text-fg-muted">Event type governs — see SS1</span>
            </div>
          )}
          {view.index > 0 && (
            // SS1 (stage 0) is the event's base stage and — on circuit
            // events — owns the lap_line gate that times live laps;
            // deleting it would silently kill lap timing (review).
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDeleteStage}
              className="gap-1 text-status-error hover:text-status-error"
              title="Remove this stage's metadata and delete its gates"
            >
              <Trash2 className="size-4" />
              Delete stage
            </Button>
          )}
        </CardContent>
      </Card>

      {/* 2 — Schedule */}
      <Card className="p-4">
        <CardContent className="p-0">
          <h3 className="mb-3 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-fg-secondary">
            <span>Schedule</span>
            <span className="font-mono text-[11px] font-normal normal-case tracking-normal text-fg-muted">
              {event.timezone || 'event-local'}
            </span>
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Stage date" unit="optional">
              <Input
                type="date"
                value={form.date}
                onChange={(e) => patch({ date: e.target.value })}
                className="font-mono text-xs"
              />
            </Field>
            <Field label={isCircuit ? 'Heat starts' : 'First car starts'} unit="hh:mm">
              <Input
                value={form.startTime}
                onChange={(e) => patch({ startTime: e.target.value })}
                placeholder="10:38"
                className="font-mono text-xs"
                inputMode="numeric"
                maxLength={5}
              />
            </Field>
            {isCircuit ? (
              <Field label="Laps" unit="laps">
                <Input
                  type="number"
                  min={1}
                  max={999}
                  step={1}
                  value={form.laps}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) patch({ laps: clamp(Math.round(n), 1, 999) });
                  }}
                  className="font-mono text-xs"
                />
              </Field>
            ) : (
              <Field label="Car interval" unit="min">
                <CarIntervalSelect
                  seconds={form.carIntervalSeconds}
                  onChange={(s) => patch({ carIntervalSeconds: s })}
                />
              </Field>
            )}
            <Field label="Safety timeout" unit="min · auto-stop">
              <Input
                type="number"
                min={TIMEOUT_MIN_MINUTES}
                max={TIMEOUT_MAX_MINUTES}
                step={1}
                value={form.timeoutMinutes}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isFinite(n)) {
                    patch({
                      timeoutMinutes: clamp(Math.round(n), TIMEOUT_MIN_MINUTES, TIMEOUT_MAX_MINUTES),
                    });
                  }
                }}
                className="font-mono text-xs"
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* 3 — Gate panes (paste-friendly; ABOVE the map so coordinates
          are reachable without scrolling past it) */}
      <div className="grid gap-3.5 lg:grid-cols-2">
        <GatePane kind="start" value={form.start} onChange={setStart} />
        <GatePane kind="finish" value={form.finish} onChange={setFinish} />
      </div>

      {/* 4 — Route + gates map: ONE unified editor (review feedback — two
          stacked maps were confusing). CircuitMapEditor hosts track
          drawing + route upload + lap-line tools + gate placement;
          gate clicks update the panes above (page-level Save changes
          persists them via the per-gate diff-save). Tool gating keeps
          the earlier safety scoping: route/lap-line tools only on
          stage 0 (the route + lap line are event-global), and lap-line
          tools only on Circuit-typed EVENTS. */}
      <Card className="p-4">
        <CardContent className="p-0">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-secondary">
            Route + gates
          </h3>
          <CircuitMapEditor
            event={event}
            onSaved={onRefresh}
            start={form.start}
            finish={form.finish}
            onStartChange={({ lat, lon }) => setStart({ ...form.start, lat, lon })}
            onFinishChange={({ lat, lon }) => setFinish({ ...form.finish, lat, lon })}
            routeTools={view.index === 0}
            lapLineTools={
              (event.type ?? '').trim().toLowerCase() === 'circuit' && view.index === 0
            }
          />
          {view.index > 0 && (
            <p className="mt-2 text-xs text-fg-muted">
              Route/track drawing is shared across the whole event — draw or upload it
              on SS1.
            </p>
          )}
        </CardContent>
      </Card>

      {/* 4b — M5 corridor route: GPX upload → server decimates →
          phones get "anything inside the corridor belongs to this
          stage". Compact v1 (no map overlay yet). */}
      <StageCorridorCard
        eventId={event.id}
        stageIndex={view.index}
        onRefresh={onRefresh}
      />

      {/* 5 — Sessions recorded on this stage's date (Epic #93 MVP —
          scoped server-side by the stage date in the event timezone).
          Read-only; set the stage date in the Schedule card above.
          Below the map on purpose: browse content sits under the
          editing surfaces (gates above the map — PR #109). */}
      <StageSessionsCard
        eventId={event.id}
        stageIndex={view.index}
        resolvedDate={view.date ?? event.active_from.slice(0, 10)}
      />

      {/* 6 — per-driver footage downloads over the same session scope
          as the card above. */}
      <StageFootageCard eventId={event.id} stageIndex={view.index} />
    </div>
  );
};

export default StageEditorPane;

// ── M5 corridor card ──────────────────────────────────────────────────

/** Parse GPX text → [[lat, lon], …] from trkpt/rtept/wpt in document
 *  order. DOMParser only — no dependency; malformed files → []. */
function parseGpxPoints(text: string): Array<[number, number]> {
  try {
    const doc = new DOMParser().parseFromString(text, 'application/xml');
    if (doc.querySelector('parsererror')) return [];
    const nodes = doc.querySelectorAll('trkpt, rtept, wpt');
    const out: Array<[number, number]> = [];
    nodes.forEach((n) => {
      const lat = Number(n.getAttribute('lat'));
      const lon = Number(n.getAttribute('lon'));
      if (Number.isFinite(lat) && Number.isFinite(lon)) out.push([lat, lon]);
    });
    return out;
  } catch {
    return [];
  }
}

const StageCorridorCard: React.FC<{
  eventId: number;
  stageIndex: number;
  /** Refetch the event after save/delete so stage_route_indices (and
   *  the stage's Draft/Live status chip) track the route's existence. */
  onRefresh: () => Promise<void> | void;
}> = ({ eventId, stageIndex, onRefresh }) => {
  const [info, setInfo] = React.useState<StageRouteInfo | null>(null);
  const [corridorM, setCorridorM] = React.useState('50');
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setInfo(null);
    setMsg(null);
    void getStageRoute(eventId, stageIndex)
      .then((r) => {
        if (cancelled) return;
        setInfo(r);
        if (r) setCorridorM(String(r.corridor_m));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId, stageIndex]);

  const onFile = async (file: File) => {
    setBusy(true);
    setMsg(null);
    try {
      const points = parseGpxPoints(await file.text());
      if (points.length < 2) {
        setMsg('No track points found in that GPX.');
        return;
      }
      const width = clamp(Math.round(Number(corridorM) || 50), 10, 1000);
      const saved = await putStageRoute(eventId, stageIndex, points, width);
      setInfo(saved);
      setMsg(`Saved — ${saved.point_count} pts (from ${points.length}), phones re-syncing.`);
      await onRefresh();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'route upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-4">
      <CardContent className="p-0">
        <h3 className="mb-2 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-fg-secondary">
          <span>Corridor route</span>
          {info && (
            <span className="font-mono text-[11px] font-normal normal-case tracking-normal text-fg-muted">
              {info.point_count} pts · {info.length_m != null ? `${(info.length_m / 1000).toFixed(1)} km` : '—'} · ±{info.corridor_m} m
            </span>
          )}
        </h3>
        <p className="mb-2 text-xs text-fg-muted">
          Upload the stage GPX — anything inside the corridor belongs to this stage
          (start = corridor entry at the route start; stop = route end, corridor exit,
          or timeout). Gates stay as fallback. Saving pushes to phones.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="file"
            accept=".gpx,application/gpx+xml"
            disabled={busy}
            className="text-xs file:mr-2 file:rounded file:border file:border-border file:bg-bg-input file:px-2 file:py-1 file:text-xs file:text-fg"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
              e.target.value = '';
            }}
          />
          <div className="flex items-center gap-1.5">
            <Label className="text-xs text-fg-secondary">Width</Label>
            <Input
              type="number"
              min={10}
              max={1000}
              step={10}
              value={corridorM}
              onChange={(e) => setCorridorM(e.target.value)}
              className="h-7 w-[72px] font-mono text-xs"
              aria-label="Corridor width in metres"
            />
            <span className="text-[11px] text-fg-muted">m</span>
          </div>
          {info && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              className="h-7 gap-1 text-status-error hover:text-status-error"
              onClick={() => {
                setBusy(true);
                void deleteStageRoute(eventId, stageIndex)
                  .then(async () => {
                    setInfo(null);
                    setMsg('Corridor removed — phones re-syncing.');
                    await onRefresh();
                  })
                  .catch((err) =>
                    setMsg(err instanceof Error ? err.message : 'delete failed'),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              <Trash2 className="size-3.5" />
              Remove
            </Button>
          )}
        </div>
        {msg && <p className="mt-2 text-xs text-fg-secondary">{msg}</p>}
      </CardContent>
    </Card>
  );
};

// ── Sub-components ────────────────────────────────────────────────────

const WarnBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-center gap-2.5 rounded border border-status-warning/40 bg-status-warning/10 px-3 py-2.5 text-xs text-fg">
    <TriangleAlert className="size-4 shrink-0 text-status-warning" />
    <span>{children}</span>
  </div>
);

/** Stage>0's read-only echo of the resolved stage type — the event-type
 *  selector on SS1 is the only thing that ever changes it. */
const SegmentedTypeToggle: React.FC<{
  value: StageType;
  disabled: true;
}> = ({ value }) => (
  <div className="flex overflow-hidden rounded border border-border bg-bg-input opacity-60">
    {(['Stage', 'Circuit'] as const).map((t) => (
      <span
        key={t}
        className={cn(
          'px-3.5 py-1.5 text-xs font-semibold',
          value === t ? 'bg-accent font-bold text-bg' : 'text-fg-muted',
        )}
      >
        {t}
      </span>
    ))}
  </div>
);

/** Full event-type selector (SS1 only) — patches event.type, the field
 *  everything real keys off, not the stage-local Stage/Circuit split. */
const EventTypeSelect: React.FC<{
  value: string;
  onChange: (type: string) => void | Promise<void>;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="h-9 w-[168px] text-xs">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {/* A stored type outside EVENT_TYPES rendered the trigger blank —
          keep the current value selectable (mirrors EventTab's guard). */}
      {value && !EVENT_TYPES.includes(value as (typeof EVENT_TYPES)[number]) && (
        <SelectItem value={value}>{value}</SelectItem>
      )}
      {EVENT_TYPES.map((t) => (
        <SelectItem key={t} value={t}>
          {t}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const Field: React.FC<{ label: string; unit: string; children: React.ReactNode }> = ({
  label,
  unit,
  children,
}) => (
  <div className="space-y-1">
    <Label className="text-xs text-fg-secondary">{label}</Label>
    <div className="flex items-center gap-1.5">
      <div className="flex-1">{children}</div>
      <span className="shrink-0 whitespace-nowrap text-[11px] text-fg-muted">{unit}</span>
    </div>
  </div>
);

const CarIntervalSelect: React.FC<{
  seconds: number;
  onChange: (seconds: number) => void;
}> = ({ seconds, onChange }) => {
  // Legacy/hand-edited metadata may hold a non-standard interval —
  // surface it as an extra option so the select doesn't render empty.
  const options = CAR_INTERVAL_OPTIONS.some((o) => o.seconds === seconds)
    ? CAR_INTERVAL_OPTIONS
    : [
        ...CAR_INTERVAL_OPTIONS,
        {
          seconds,
          label: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`,
        },
      ];
  return (
    <Select value={String(seconds)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-10 font-mono text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.seconds} value={String(o.seconds)} className="font-mono text-xs">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const fmtTime = (ms: number | null): string =>
  ms == null ? '—' : new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const StageSessionsCard: React.FC<{
  eventId: number;
  stageIndex: number;
  resolvedDate: string;
}> = ({ eventId, stageIndex, resolvedDate }) => {
  const { sessions, loading, error } = useStageSessions(eventId, stageIndex);
  return (
    <Card className="p-4">
      <CardContent className="p-0">
        <h3 className="mb-2 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wider text-fg-secondary">
          <span>Sessions</span>
          <span className="font-mono text-[11px] font-normal normal-case tracking-normal text-fg-muted">
            {resolvedDate}
          </span>
        </h3>
        {error ? (
          <p className="text-xs text-status-error">{error}</p>
        ) : loading && sessions.length === 0 ? (
          <p className="text-xs text-fg-muted">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="text-xs text-fg-muted">No sessions recorded on this date.</p>
        ) : (
          <ul className="divide-y divide-border text-xs">
            {sessions.map((s) => (
              <li key={s.session_id} className="flex items-center justify-between gap-3 py-1.5">
                {/* M4 — car number inherited via the enrollment; the
                    operator reads "car 12", not a device hash. */}
                {s.car_number && (
                  <span className="shrink-0 rounded bg-bg-input px-1.5 py-0.5 font-mono text-[11px] font-semibold text-fg">
                    #{s.car_number}
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate font-mono text-fg" title={s.session_id}>
                  {s.session_id}
                </span>
                {/* M3: attributed rows are the authoritative linkage;
                    date-matched rows (null) show without the tick. */}
                {s.stage_index != null && (
                  <span
                    className="shrink-0 text-[10px] uppercase text-status-online"
                    title="Attributed to this stage (device-reported or fallback attribution)"
                  >
                    ✓ stage
                  </span>
                )}
                <span className="shrink-0 font-mono text-fg-secondary">{fmtTime(s.start_utc_ms)}</span>
                <span
                  className={cn(
                    'shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase',
                    s.state === 'recording'
                      ? 'bg-status-online/15 text-status-online'
                      : 'bg-bg-input text-fg-muted',
                  )}
                >
                  {s.state}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
